import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import {
  Building2,
  Copy,
  Globe,
  Hash,
  Image as ImageIcon,
  KeyRound,
  ListOrdered,
  Loader2,
  Pencil,
  Plus,
  Power,
  ShieldCheck,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FiscalAccessDenied } from '@/components/fiscal/FiscalAccessDenied';
import { shortenMiddle } from '@/components/fiscal/fiscalFormat';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import { useFiscalApi } from '@/services/api';
import { t } from '@/i18n';
import { ENV_BADGE_CLASS } from './fiscalEnv';
import {
  KSEF_ENVIRONMENTS,
  type FiscalApiTokenDto,
  type FiscalNumberingCounterDto,
  type FiscalSettingsDto,
  type KsefEnvironment,
} from '@ip/shared-types';

const PREFIX_RE = /^[A-Za-z0-9]{1,10}$/;
const CERT_EXTENSIONS = ['.p12', '.pfx', '.pem', '.crt'];
const MIN_COUNTER_YEAR = 2020;
const MAX_COUNTER_YEAR = 2100;
const MAX_COUNTER_SEQ = 999999;

const warsawYearMonthFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
});

/** Current Warsaw-local {year, month} — matches the backend's numbering bucket derivation. */
function warsawYearMonth(): { year: number; month: number } {
  const parts = warsawYearMonthFmt.formatToParts(new Date());
  return {
    year: Number(parts.find((p) => p.type === 'year')!.value),
    month: Number(parts.find((p) => p.type === 'month')!.value),
  };
}

/** Radix Select nie przyjmuje pustego stringa jako wartości pozycji — sentinel
 *  reprezentuje "domyślne środowisko firmy" (null w API). */
const ENV_COMPANY_DEFAULT = '__company_default__';

function counterKey(c: { prefix: string; year: number; month: number; environment: string }): string {
  // Środowisko MUSI być częścią klucza: ta sama seria (prefix+rok+miesiąc) może
  // istnieć osobno dla demo/test/produkcji — bez env optimistic merge po seedzie
  // nadpisywałby wiersz innego środowiska (i duplikował klucze Reacta).
  return `${c.environment}|${c.prefix}|${c.year}|${c.month}`;
}

/** Ten sam porządek co backendowy getCounters (rok/miesiąc malejąco, prefix i
 *  środowisko rosnąco) — optimistic merge po seedzie musi go zachować, bo
 *  widok "najnowszy miesiąc serii" bierze PIERWSZY wiersz każdej serii. */
function sortCounters(list: FiscalNumberingCounterDto[]): FiscalNumberingCounterDto[] {
  return [...list].sort(
    (a, b) =>
      b.year - a.year ||
      b.month - a.month ||
      a.prefix.localeCompare(b.prefix) ||
      a.environment.localeCompare(b.environment),
  );
}

/** PREFIX/NNNN/MM/YYYY preview of the number the NEXT assignNumber() call would produce. */
function nextNumberPreview(c: { prefix: string; year: number; month: number; lastSeq: number }): string {
  const seq = c.lastSeq + 1;
  return `${c.prefix}/${String(seq).padStart(4, '0')}/${String(c.month).padStart(2, '0')}/${c.year}`;
}

/** Parse a comma-separated numbering-prefix input into a trimmed, non-empty list. */
function parsePrefixList(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
const CERT_MAX_BYTES = 10 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg'];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

interface MainFormValues {
  enabled: boolean;
  autoInvoiceNum: boolean;
  invoicePrefix: string;
  sellerName: string;
  sellerAddressLine1: string;
  sellerAddressLine2: string;
  sellerCountryCode: string;
}

function formFromSettings(settings: FiscalSettingsDto): MainFormValues {
  return {
    enabled: settings.enabled,
    autoInvoiceNum: settings.autoInvoiceNum,
    invoicePrefix: settings.invoicePrefix ?? '',
    sellerName: settings.sellerName ?? '',
    sellerAddressLine1: settings.sellerAddressLine1 ?? '',
    sellerAddressLine2: settings.sellerAddressLine2 ?? '',
    sellerCountryCode: settings.sellerCountryCode || 'PL',
  };
}

type CertExpiryStatus = 'expired' | 'soon' | 'ok' | 'unknown';

function certExpiryStatus(expiry: string | null): { status: CertExpiryStatus; days: number | null } {
  if (!expiry) return { status: 'unknown', days: null };
  const days = dayjs(expiry).diff(dayjs(), 'day');
  if (days < 0) return { status: 'expired', days };
  if (days <= 30) return { status: 'soon', days };
  return { status: 'ok', days };
}

/** Selektor środowiska KSeF dla tokena API (create + edit): "Domyślne firmy"
 *  (sentinel → null w API) albo jawny override. Produkcja pokazuje prawnie
 *  wiążące ostrzeżenie; `warning` to komunikat o brakującym/wygasłym
 *  certyfikacie dla wybranego środowiska. */
function TokenEnvSelect({
  id,
  value,
  onChange,
  warning,
  companyDefaultEnv,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  warning: string | null;
  companyDefaultEnv: KsefEnvironment;
}) {
  // Ostrzeżenie produkcyjne wg środowiska EFEKTYWNEGO: "Domyślne firmy" przy
  // firmie ustawionej na produkcję też wystawia prawnie wiążące faktury.
  const effectiveEnv = value === ENV_COMPANY_DEFAULT ? companyDefaultEnv : (value as KsefEnvironment);
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{t('fiscal.settings.tokenEnvironmentLabel')}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ENV_COMPANY_DEFAULT}>{t('fiscal.settings.tokenEnvironmentDefault')}</SelectItem>
          {KSEF_ENVIRONMENTS.map((env) => (
            <SelectItem key={env} value={env}>
              {t(`fiscal.environment.${env}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-xs">{t('fiscal.settings.tokenEnvironmentHelp')}</p>
      {effectiveEnv === 'production' && (
        <p className="text-destructive text-xs">{t('fiscal.settings.tokenEnvironmentProductionWarning')}</p>
      )}
      {warning && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-xs text-amber-700 dark:text-amber-400">{warning}</p>
        </div>
      )}
    </div>
  );
}

async function copyToClipboard(value: string, successKey: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(t(successKey));
  } catch {
    toast.error(t('common.error'));
  }
}

/**
 * Fiscal module settings: enable switch, KSeF environment (admin-confirmed —
 * production carries a legally-binding warning), per-environment certificates,
 * invoice numbering, seller data, API tokens (one-time reveal on create,
 * mirroring the printer token flow) and the receipt-PDF logo. Mutations are
 * gated to company admins (checkCompanyRole(['admin']) on the backend);
 * ksiegowy/user see the same page read-only.
 */
export function FiscalSettings() {
  const { nip } = useOutletContext<AppShellContext>();
  const { isSuperAdmin, permissions } = useAuth();
  const isAdmin = isSuperAdmin || hasCompanyRole(permissions, nip, ['admin']);
  const {
    getSettings,
    updateSettings,
    uploadCertificate,
    deleteCertificate,
    listApiTokens,
    createApiToken,
    updateApiToken,
    deleteApiToken,
    uploadLogo,
    deleteLogo,
    getNumberingCounters,
    seedNumberingCounter,
  } = useFiscalApi();

  const [settings, setSettings] = useState<FiscalSettingsDto | null>(null);
  const [tokens, setTokens] = useState<FiscalApiTokenDto[]>([]);
  const [counters, setCounters] = useState<FiscalNumberingCounterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const loadSeq = useRef(0);

  const [form, setForm] = useState<MainFormValues>({
    enabled: false,
    autoInvoiceNum: false,
    invoicePrefix: '',
    sellerName: '',
    sellerAddressLine1: '',
    sellerAddressLine2: '',
    sellerCountryCode: 'PL',
  });
  const [saving, setSaving] = useState(false);

  const [pendingEnv, setPendingEnv] = useState<KsefEnvironment | null>(null);
  const [envSubmitting, setEnvSubmitting] = useState(false);

  const [certUploadEnv, setCertUploadEnv] = useState<KsefEnvironment | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPassword, setCertPassword] = useState('');
  const [certUploading, setCertUploading] = useState(false);

  const [certDeleteEnv, setCertDeleteEnv] = useState<KsefEnvironment | null>(null);
  const [certDeleting, setCertDeleting] = useState(false);

  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenPrefixesInput, setTokenPrefixesInput] = useState('');
  const [tokenEnvValue, setTokenEnvValue] = useState<string>(ENV_COMPANY_DEFAULT);
  const [tokenCreating, setTokenCreating] = useState(false);
  const [tokenReveal, setTokenReveal] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<FiscalApiTokenDto | null>(null);
  const [revoking, setRevoking] = useState(false);

  const [editTarget, setEditTarget] = useState<FiscalApiTokenDto | null>(null);
  const [editName, setEditName] = useState('');
  const [editPrefixesInput, setEditPrefixesInput] = useState('');
  const [editEnvValue, setEditEnvValue] = useState<string>(ENV_COMPANY_DEFAULT);
  const [editSaving, setEditSaving] = useState(false);

  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoPreviewRef = useRef<string | null>(null);

  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [counterPrefix, setCounterPrefix] = useState('');
  const [counterYear, setCounterYear] = useState('');
  const [counterMonth, setCounterMonth] = useState('');
  const [counterLastSeq, setCounterLastSeq] = useState('');
  // Bez preselekcji — seed to narzędzie migracyjne i zły default (np. demo
  // zamiast production) jest groźny; admin musi świadomie wybrać środowisko.
  const [counterEnv, setCounterEnv] = useState<string>('');
  const [counterSubmitting, setCounterSubmitting] = useState(false);
  const [showCounterHistory, setShowCounterHistory] = useState(false);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const [settingsResult, tokensResult, countersResult] = await Promise.all([
        getSettings(nip),
        listApiTokens(nip),
        getNumberingCounters(nip),
      ]);
      if (seq !== loadSeq.current) return;
      setSettings(settingsResult);
      setForm(formFromSettings(settingsResult));
      setTokens(tokensResult);
      setCounters(countersResult);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(t('fiscal.settings.loadFailed'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, getSettings, listApiTokens, getNumberingCounters]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (logoPreviewRef.current) URL.revokeObjectURL(logoPreviewRef.current);
    },
    [],
  );

  const prefixError =
    form.invoicePrefix && !PREFIX_RE.test(form.invoicePrefix) ? t('fiscal.settings.prefixInvalid') : null;
  const prefixExample = `${(form.invoicePrefix || 'PREFIX').toUpperCase()}/0001/${dayjs().format('MM/YYYY')}`;

  const handleSaveMain = async () => {
    if (form.invoicePrefix && !PREFIX_RE.test(form.invoicePrefix)) {
      toast.error(t('fiscal.settings.prefixInvalid'));
      return;
    }
    setSaving(true);
    try {
      const updated = await updateSettings(nip, {
        enabled: form.enabled,
        autoInvoiceNum: form.autoInvoiceNum,
        invoicePrefix: form.invoicePrefix.trim() || null,
        sellerName: form.sellerName.trim() || null,
        sellerAddressLine1: form.sellerAddressLine1.trim() || null,
        sellerAddressLine2: form.sellerAddressLine2.trim() || null,
        sellerCountryCode: form.sellerCountryCode.trim() || 'PL',
      });
      setSettings(updated);
      setForm(formFromSettings(updated));
      toast.success(t('fiscal.settings.saveSuccess'));
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnvironmentConfirm = async () => {
    if (!pendingEnv) return;
    setEnvSubmitting(true);
    try {
      const updated = await updateSettings(nip, { environment: pendingEnv });
      setSettings(updated);
      toast.success(t('fiscal.settings.environmentUpdateSuccess'));
      setPendingEnv(null);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.settings.environmentUpdateFailed'));
    } finally {
      setEnvSubmitting(false);
    }
  };

  const openCertUpload = (env: KsefEnvironment) => {
    setCertUploadEnv(env);
    setCertFile(null);
    setCertPassword('');
  };

  const certFileExt = certFile
    ? certFile.name.toLowerCase().slice(certFile.name.toLowerCase().lastIndexOf('.'))
    : null;
  const certFileFormat = certFileExt === '.p12' || certFileExt === '.pfx' ? 'p12' : certFileExt ? 'pem' : null;

  const handleCertUpload = async () => {
    if (!certUploadEnv || !certFile) return;
    const ext = certFile.name.toLowerCase().slice(certFile.name.toLowerCase().lastIndexOf('.'));
    if (!CERT_EXTENSIONS.includes(ext)) {
      toast.error(t('fiscal.settings.certificateWrongType'));
      return;
    }
    if (certFile.size > CERT_MAX_BYTES) {
      toast.error(t('fiscal.settings.certificateTooLarge'));
      return;
    }
    setCertUploading(true);
    try {
      const info = await uploadCertificate(nip, certUploadEnv, certFile, certPassword || undefined);
      setSettings((prev) => {
        if (!prev) return prev;
        const certificates = [...prev.certificates.filter((c) => c.environment !== certUploadEnv), info];
        return { ...prev, certificates };
      });
      toast.success(t('fiscal.settings.certificateUploadSuccess'));
      setCertUploadEnv(null);
      setCertFile(null);
      setCertPassword('');
    } catch (error) {
      toast.error(
        error instanceof Error && error.message ? error.message : t('fiscal.settings.certificateUploadFailed'),
      );
    } finally {
      setCertUploading(false);
    }
  };

  const handleCertDelete = async () => {
    if (!certDeleteEnv) return;
    setCertDeleting(true);
    try {
      await deleteCertificate(nip, certDeleteEnv);
      setSettings((prev) =>
        prev ? { ...prev, certificates: prev.certificates.filter((c) => c.environment !== certDeleteEnv) } : prev,
      );
      toast.success(t('fiscal.settings.certificateDeleteSuccess'));
      setCertDeleteEnv(null);
    } catch (error) {
      toast.error(
        error instanceof Error && error.message ? error.message : t('fiscal.settings.certificateDeleteFailed'),
      );
    } finally {
      setCertDeleting(false);
    }
  };

  const handleCreateToken = async () => {
    if (!tokenName.trim()) {
      toast.error(t('fiscal.settings.tokenNameRequired'));
      return;
    }
    const prefixes = parsePrefixList(tokenPrefixesInput);
    if (prefixes.some((p) => !PREFIX_RE.test(p))) {
      toast.error(t('fiscal.settings.tokenPrefixesInvalid'));
      return;
    }
    setTokenCreating(true);
    try {
      const environment = tokenEnvValue === ENV_COMPANY_DEFAULT ? null : (tokenEnvValue as KsefEnvironment);
      const { token, dto } = await createApiToken(nip, tokenName.trim(), prefixes, environment);
      setTokens((prev) => [dto, ...prev]);
      setTokenDialogOpen(false);
      setTokenName('');
      setTokenPrefixesInput('');
      setTokenEnvValue(ENV_COMPANY_DEFAULT);
      toast.success(t('fiscal.settings.tokenCreateSuccess'));
      setTokenReveal(token);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.settings.tokenCreateFailed'));
    } finally {
      setTokenCreating(false);
    }
  };

  const openEditToken = (tok: FiscalApiTokenDto) => {
    setEditTarget(tok);
    setEditName(tok.name);
    setEditPrefixesInput(tok.numberingPrefixes.join(', '));
    setEditEnvValue(tok.environment ?? ENV_COMPANY_DEFAULT);
  };

  const handleUpdateToken = async () => {
    if (!editTarget) return;
    if (!editName.trim()) {
      toast.error(t('fiscal.settings.tokenNameRequired'));
      return;
    }
    const prefixes = parsePrefixList(editPrefixesInput);
    if (prefixes.some((p) => !PREFIX_RE.test(p))) {
      toast.error(t('fiscal.settings.tokenPrefixesInvalid'));
      return;
    }
    setEditSaving(true);
    try {
      const dto = await updateApiToken(nip, editTarget.id, {
        name: editName.trim(),
        numberingPrefixes: prefixes,
        environment: editEnvValue === ENV_COMPANY_DEFAULT ? null : (editEnvValue as KsefEnvironment),
      });
      setTokens((prev) => prev.map((tok) => (tok.id === dto.id ? dto : tok)));
      setEditTarget(null);
      toast.success(t('fiscal.settings.tokenUpdateSuccess'));
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.settings.tokenUpdateFailed'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await deleteApiToken(nip, revokeTarget.id);
      setTokens((prev) => prev.filter((tok) => tok.id !== revokeTarget.id));
      toast.success(t('fiscal.settings.revokeSuccess'));
      setRevokeTarget(null);
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.settings.revokeFailed'));
    } finally {
      setRevoking(false);
    }
  };

  const handleLogoFile = async (file: File) => {
    if (!LOGO_TYPES.includes(file.type)) {
      toast.error(t('fiscal.settings.logoWrongType'));
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error(t('fiscal.settings.logoTooLarge'));
      return;
    }
    setLogoBusy(true);
    try {
      const updated = await uploadLogo(nip, file);
      setSettings(updated);
      if (logoPreviewRef.current) URL.revokeObjectURL(logoPreviewRef.current);
      const url = URL.createObjectURL(file);
      logoPreviewRef.current = url;
      setLogoPreviewUrl(url);
      toast.success(t('fiscal.settings.logoUploadSuccess'));
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.settings.logoUploadFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoBusy(true);
    try {
      const updated = await deleteLogo(nip);
      setSettings(updated);
      if (logoPreviewRef.current) {
        URL.revokeObjectURL(logoPreviewRef.current);
        logoPreviewRef.current = null;
      }
      setLogoPreviewUrl(null);
      toast.success(t('fiscal.settings.logoRemoveSuccess'));
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.settings.logoRemoveFailed'));
    } finally {
      setLogoBusy(false);
    }
  };

  /** Ostrzeżenie przy wyborze środowiska tokena: brak certyfikatu albo cert
   *  wygasł → faktury tego tokena nie wyjdą do KSeF (submit dostaje 400). */
  const envCertWarning = (value: string): string | null => {
    if (!settings) return null;
    const env = (value === ENV_COMPANY_DEFAULT ? settings.environment : value) as KsefEnvironment;
    const cert = settings.certificates.find((c) => c.environment === env) ?? null;
    if (!cert) return t('fiscal.settings.tokenEnvNoCertificate', { environment: t(`fiscal.environment.${env}`) });
    if (certExpiryStatus(cert.expiry).status === 'expired') {
      return t('fiscal.settings.tokenEnvExpiredCertificate', { environment: t(`fiscal.environment.${env}`) });
    }
    return null;
  };

  const openCounterDialog = () => {
    const { year, month } = warsawYearMonth();
    setCounterPrefix(form.invoicePrefix || '');
    setCounterYear(String(year));
    setCounterMonth(String(month));
    setCounterLastSeq('');
    setCounterEnv('');
    setCounterDialogOpen(true);
  };

  const counterPrefixValue = counterPrefix.trim();
  const counterYearNum = Number(counterYear);
  const counterMonthNum = Number(counterMonth);
  const counterLastSeqNum = Number(counterLastSeq);
  const counterPrefixError =
    counterPrefixValue && !PREFIX_RE.test(counterPrefixValue) ? t('fiscal.settings.prefixInvalid') : null;
  const counterFormValid =
    !!counterPrefixValue &&
    (KSEF_ENVIRONMENTS as readonly string[]).includes(counterEnv) &&
    PREFIX_RE.test(counterPrefixValue) &&
    Number.isInteger(counterYearNum) &&
    counterYearNum >= MIN_COUNTER_YEAR &&
    counterYearNum <= MAX_COUNTER_YEAR &&
    Number.isInteger(counterMonthNum) &&
    counterMonthNum >= 1 &&
    counterMonthNum <= 12 &&
    counterLastSeq !== '' &&
    Number.isInteger(counterLastSeqNum) &&
    counterLastSeqNum >= 0 &&
    counterLastSeqNum <= MAX_COUNTER_SEQ;
  const counterPreview = counterFormValid
    ? nextNumberPreview({ prefix: counterPrefixValue.toUpperCase(), year: counterYearNum, month: counterMonthNum, lastSeq: counterLastSeqNum })
    : null;

  // Numeracja jest MIESIĘCZNA (jeden wiersz licznika na prefix+środowisko+miesiąc),
  // więc tabela rośnie z każdym miesiącem. Domyślnie pokazujemy tylko najnowszy
  // miesiąc każdej serii; getCounters sortuje rok/miesiąc malejąco, więc pierwszy
  // napotkany wiersz serii jest jej najnowszym.
  const latestCounters = (() => {
    const seen = new Set<string>();
    const out: FiscalNumberingCounterDto[] = [];
    for (const c of counters) {
      const seriesKey = `${c.environment}|${c.prefix}`;
      if (!seen.has(seriesKey)) {
        seen.add(seriesKey);
        out.push(c);
      }
    }
    return out;
  })();
  const visibleCounters = showCounterHistory ? counters : latestCounters;
  const hiddenCounterCount = counters.length - latestCounters.length;

  const handleSetCounter = async () => {
    if (!counterFormValid) return;
    setCounterSubmitting(true);
    try {
      const dto = await seedNumberingCounter(nip, {
        prefix: counterPrefixValue,
        year: counterYearNum,
        month: counterMonthNum,
        lastSeq: counterLastSeqNum,
        environment: counterEnv as KsefEnvironment,
      });
      setCounters((prev) => {
        const idx = prev.findIndex((c) => counterKey(c) === counterKey(dto));
        if (idx === -1) return sortCounters([dto, ...prev]);
        const next = [...prev];
        next[idx] = dto;
        return next;
      });
      toast.success(t('fiscal.settings.setCounterSuccess'));
      setCounterDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('move forward')) {
        toast.error(t('fiscal.settings.counterBackwardError'));
      } else {
        toast.error(message || t('fiscal.settings.setCounterFailed'));
      }
    } finally {
      setCounterSubmitting(false);
    }
  };

  if (forbidden) return <FiscalAccessDenied />;

  if (loading || !settings) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {!isAdmin && <p className="text-muted-foreground text-sm">{t('fiscal.settings.readOnlyNote')}</p>}

      {/* (a) Module enable */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Power className="size-4" />
            {t('fiscal.settings.moduleTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="fiscal-enabled">{t('fiscal.settings.moduleEnabled')}</Label>
              <p className="text-muted-foreground text-xs">{t('fiscal.settings.moduleHint')}</p>
            </div>
            <Switch
              id="fiscal-enabled"
              checked={form.enabled}
              disabled={!isAdmin}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, enabled: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* (b) Environment */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Globe className="size-4" />
            {t('fiscal.settings.environmentTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4">
          <RadioGroup
            value={settings.environment}
            onValueChange={(v) => {
              if (!isAdmin || v === settings.environment) return;
              setPendingEnv(v as KsefEnvironment);
            }}
          >
            {KSEF_ENVIRONMENTS.map((env) => (
              <div key={env} className="flex items-start gap-2">
                <RadioGroupItem value={env} id={`env-${env}`} disabled={!isAdmin} className="mt-0.5" />
                <Label htmlFor={`env-${env}`} className="flex-1 cursor-pointer font-normal">
                  <span className="font-medium">{t(`fiscal.environment.${env}`)}</span>
                  <p className="text-muted-foreground text-xs">{t(`fiscal.settings.environmentHint.${env}`)}</p>
                </Label>
              </div>
            ))}
          </RadioGroup>
          <p className="text-muted-foreground mt-3 text-xs">
            {t('fiscal.settings.environmentDefaultNote')}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {t('fiscal.settings.environmentSyncNote')}
          </p>
        </CardContent>
      </Card>

      {/* (c) Certificates */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheck className="size-4" />
            {t('fiscal.settings.certificatesTitle')}
          </CardTitle>
          <p className="text-muted-foreground text-xs">{t('fiscal.settings.certificatesHint')}</p>
        </CardHeader>
        <CardContent className="space-y-3 px-4">
          {KSEF_ENVIRONMENTS.map((env) => {
            const cert = settings.certificates.find((c) => c.environment === env) ?? null;
            const expiry = cert ? certExpiryStatus(cert.expiry) : null;
            return (
              <div key={env} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t(`fiscal.environment.${env}`)}</p>
                  {cert ? (
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      {cert.fingerprint && (
                        <span className="text-muted-foreground font-mono">
                          {t('fiscal.settings.certificateFingerprint')}: {shortenMiddle(cert.fingerprint, 8, 6)}
                        </span>
                      )}
                      {cert.format && <Badge variant="outline">{cert.format}</Badge>}
                      {cert.expiry && expiry && (
                        <Badge
                          variant="outline"
                          className={
                            expiry.status === 'expired'
                              ? 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400'
                              : expiry.status === 'soon'
                                ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                                : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                          }
                        >
                          {expiry.status === 'expired'
                            ? t('fiscal.settings.certificateExpired')
                            : expiry.status === 'soon'
                              ? t('fiscal.settings.certificateExpiringSoon', { days: expiry.days ?? 0 })
                              : `${t('fiscal.settings.certificateExpiry')}: ${new Date(cert.expiry).toLocaleDateString('pl-PL')}`}
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-xs">{t('fiscal.settings.certificateMissing')}</p>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex shrink-0 gap-2">
                    <Button variant="outline" size="sm" onClick={() => openCertUpload(env)}>
                      {cert ? t('fiscal.settings.replaceCertificate') : t('fiscal.settings.uploadCertificate')}
                    </Button>
                    {cert && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive hover:text-destructive"
                        aria-label={t('fiscal.settings.deleteCertificate', { environment: t(`fiscal.environment.${env}`) })}
                        onClick={() => setCertDeleteEnv(env)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* (d) Numbering */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Hash className="size-4" />
            {t('fiscal.settings.numberingTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 px-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="fiscal-auto-num">{t('fiscal.settings.autoInvoiceNum')}</Label>
              <p className="text-muted-foreground text-xs">{t('fiscal.settings.autoInvoiceNumHint')}</p>
            </div>
            <Switch
              id="fiscal-auto-num"
              checked={form.autoInvoiceNum}
              disabled={!isAdmin}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, autoInvoiceNum: checked }))}
            />
          </div>
          <div className="max-w-xs space-y-1">
            <Label htmlFor="fiscal-prefix">{t('fiscal.settings.prefixLabel')}</Label>
            <Input
              id="fiscal-prefix"
              value={form.invoicePrefix}
              disabled={!isAdmin}
              onChange={(e) => setForm((f) => ({ ...f, invoicePrefix: e.target.value }))}
              placeholder="FV"
              maxLength={10}
              aria-invalid={prefixError ? true : undefined}
            />
            {prefixError && <p className="text-destructive text-xs">{prefixError}</p>}
            <p className="text-muted-foreground text-xs">{t('fiscal.settings.prefixPreview', { example: prefixExample })}</p>
          </div>
        </CardContent>
      </Card>

      {/* (d.2) Numbering series counters — migration-continuity seeding */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <ListOrdered className="size-4" />
              {t('fiscal.settings.countersTitle')}
            </CardTitle>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={openCounterDialog}>
                <Plus className="size-4" />
                {t('fiscal.settings.setCounter')}
              </Button>
            )}
          </div>
          <p className="text-muted-foreground text-xs">{t('fiscal.settings.countersHint')}</p>
        </CardHeader>
        <CardContent className="px-4">
          {counters.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('fiscal.settings.countersEmpty')}</p>
          ) : (
            <div className="space-y-2">
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>{t('fiscal.settings.counterPrefixColumn')}</TableHead>
                      <TableHead>{t('fiscal.settings.counterEnvironmentColumn')}</TableHead>
                      <TableHead>{t('fiscal.settings.counterPeriodColumn')}</TableHead>
                      <TableHead>{t('fiscal.settings.counterLastSeqColumn')}</TableHead>
                      <TableHead>{t('fiscal.settings.counterNextColumn')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleCounters.map((c) => (
                      <TableRow key={counterKey(c)}>
                        <TableCell className="font-mono text-xs">{c.prefix}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs font-medium uppercase ${ENV_BADGE_CLASS[c.environment]}`}>
                            {t(`fiscal.environment.${c.environment}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>{String(c.month).padStart(2, '0')}/{c.year}</TableCell>
                        <TableCell>{c.lastSeq}</TableCell>
                        <TableCell className="font-mono text-xs">{nextNumberPreview(c)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {(hiddenCounterCount > 0 || showCounterHistory) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => setShowCounterHistory((v) => !v)}
                >
                  {showCounterHistory
                    ? t('fiscal.settings.countersHideHistory')
                    : t('fiscal.settings.countersShowHistory', { count: hiddenCounterCount })}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* (e) Seller data */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Building2 className="size-4" />
            {t('fiscal.settings.sellerTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 px-4">
          <div className="space-y-1">
            <Label htmlFor="seller-name">{t('fiscal.settings.sellerName')}</Label>
            <Input
              id="seller-name"
              value={form.sellerName}
              disabled={!isAdmin}
              onChange={(e) => setForm((f) => ({ ...f, sellerName: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="seller-address1">{t('fiscal.settings.sellerAddressLine1')}</Label>
              <Input
                id="seller-address1"
                value={form.sellerAddressLine1}
                disabled={!isAdmin}
                onChange={(e) => setForm((f) => ({ ...f, sellerAddressLine1: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="seller-address2">{t('fiscal.settings.sellerAddressLine2')}</Label>
              <Input
                id="seller-address2"
                value={form.sellerAddressLine2}
                disabled={!isAdmin}
                onChange={(e) => setForm((f) => ({ ...f, sellerAddressLine2: e.target.value }))}
              />
            </div>
          </div>
          <div className="max-w-[140px] space-y-1">
            <Label htmlFor="seller-country">{t('fiscal.settings.sellerCountryCode')}</Label>
            <Input
              id="seller-country"
              value={form.sellerCountryCode}
              disabled={!isAdmin}
              maxLength={2}
              onChange={(e) => setForm((f) => ({ ...f, sellerCountryCode: e.target.value.toUpperCase() }))}
            />
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={handleSaveMain} disabled={saving || !!prefixError}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            {t('fiscal.settings.save')}
          </Button>
        </div>
      )}

      {/* (f) API tokens */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <KeyRound className="size-4" />
              {t('fiscal.settings.tokensTitle')}
            </CardTitle>
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  // Pełny reset formularza — anulowany dialog nie może przemycić
                  // starych prefiksów ani (zwłaszcza) wybranej wcześniej produkcji.
                  setTokenName('');
                  setTokenPrefixesInput('');
                  setTokenEnvValue(ENV_COMPANY_DEFAULT);
                  setTokenDialogOpen(true);
                }}
              >
                <Plus className="size-4" />
                {t('fiscal.settings.addToken')}
              </Button>
            )}
          </div>
          <p className="text-muted-foreground text-xs">{t('fiscal.settings.tokensHint')}</p>
        </CardHeader>
        <CardContent className="px-4">
          {tokens.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('fiscal.settings.tokensEmpty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>{t('fiscal.settings.tokenName')}</TableHead>
                    <TableHead>{t('fiscal.settings.tokenPrefix')}</TableHead>
                    <TableHead>{t('fiscal.settings.tokenEnvironment')}</TableHead>
                    <TableHead>{t('fiscal.settings.tokenNumberingPrefixes')}</TableHead>
                    <TableHead>{t('fiscal.settings.tokenLastUsed')}</TableHead>
                    <TableHead>{t('fiscal.settings.tokenCreated')}</TableHead>
                    {isAdmin && <TableHead className="text-right">{t('common.actions')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((tok) => (
                    <TableRow key={tok.id}>
                      <TableCell>{tok.name}</TableCell>
                      <TableCell className="font-mono text-xs">{tok.tokenPrefix}…</TableCell>
                      <TableCell>
                        {tok.environment ? (
                          <Badge className={`text-xs font-medium uppercase ${ENV_BADGE_CLASS[tok.environment]}`}>
                            {t(`fiscal.environment.${tok.environment}`)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">{t('fiscal.settings.tokenEnvironmentDefault')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {tok.numberingPrefixes.length === 0 ? (
                          <span className="text-muted-foreground text-xs">{t('fiscal.settings.tokenNumberingPrefixesDefault')}</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {tok.numberingPrefixes.map((p) => (
                              <Badge key={p} variant="secondary" className="font-mono text-xs">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {tok.lastUsedAt ? new Date(tok.lastUsedAt).toLocaleString('pl-PL') : t('fiscal.settings.tokenNeverUsed')}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(tok.createdAt).toLocaleString('pl-PL')}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={t('fiscal.settings.editToken')}
                              onClick={() => openEditToken(tok)}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-destructive hover:text-destructive"
                              aria-label={t('fiscal.settings.revoke')}
                              onClick={() => setRevokeTarget(tok)}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* (g) Receipt logo */}
      <Card className="gap-3 py-4">
        <CardHeader className="px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <ImageIcon className="size-4" />
            {t('fiscal.settings.logoTitle')}
          </CardTitle>
          <p className="text-muted-foreground text-xs">{t('fiscal.settings.logoHint')}</p>
        </CardHeader>
        <CardContent className="px-4">
          <div className="flex items-center gap-4">
            {logoBusy ? (
              <div className="bg-muted flex size-16 shrink-0 items-center justify-center rounded-lg border">
                <Loader2 className="text-muted-foreground size-5 animate-spin" />
              </div>
            ) : logoPreviewUrl ? (
              <img
                src={logoPreviewUrl}
                alt={t('fiscal.settings.logoTitle')}
                className="size-16 shrink-0 rounded-lg border object-contain"
              />
            ) : (
              <div className="bg-muted flex size-16 shrink-0 items-center justify-center rounded-lg border">
                <ImageIcon className="text-muted-foreground size-5" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              {settings.receiptLogoKey ? (
                <p className="text-sm">
                  {t('fiscal.settings.logoSet')}
                  {!logoPreviewUrl && (
                    <span className="text-muted-foreground block text-xs">{t('fiscal.settings.logoPreviewUnavailable')}</span>
                  )}
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">{t('fiscal.settings.logoNotSet')}</p>
              )}
            </div>
            {isAdmin && (
              <div className="flex shrink-0 gap-2">
                <input
                  id="fiscal-logo-input"
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  disabled={logoBusy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleLogoFile(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={logoBusy}
                  onClick={() => document.getElementById('fiscal-logo-input')?.click()}
                >
                  {settings.receiptLogoKey ? t('fiscal.settings.replaceLogo') : t('fiscal.settings.uploadLogo')}
                </Button>
                {settings.receiptLogoKey && (
                  <Button variant="outline" size="sm" disabled={logoBusy} onClick={handleLogoRemove}>
                    <Trash2 className="size-4" />
                    {t('fiscal.settings.removeLogo')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Environment change confirm */}
      <AlertDialog
        open={pendingEnv !== null}
        onOpenChange={(open) => {
          if (!open && !envSubmitting) setPendingEnv(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('fiscal.settings.environmentConfirmTitle', { environment: pendingEnv ? t(`fiscal.environment.${pendingEnv}`) : '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingEnv === 'production'
                ? t('fiscal.settings.environmentConfirmProductionWarning')
                : t('fiscal.settings.environmentConfirmGenericWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={envSubmitting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={envSubmitting}
              variant={pendingEnv === 'production' ? 'destructive' : 'default'}
              onClick={handleEnvironmentConfirm}
            >
              {envSubmitting && <Loader2 className="size-4 animate-spin" />}
              {t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Certificate upload */}
      <Dialog
        open={certUploadEnv !== null}
        onOpenChange={(open) => {
          if (!open && !certUploading) setCertUploadEnv(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('fiscal.settings.uploadCertificateTitle', { environment: certUploadEnv ? t(`fiscal.environment.${certUploadEnv}`) : '' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="cert-file">{t('fiscal.settings.certificateFile')}</Label>
              <input
                id="cert-file"
                type="file"
                accept=".p12,.pfx,.pem,.crt"
                className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
                onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
              />
              {certFileFormat && (
                <p className="text-muted-foreground text-xs">
                  {t('fiscal.settings.certificateFormatLabel')}:{' '}
                  {certFileFormat === 'p12' ? t('fiscal.settings.certificateFormatP12') : t('fiscal.settings.certificateFormatPem')}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="cert-password">{t('fiscal.settings.certificatePassword')}</Label>
              <Input
                id="cert-password"
                type="password"
                value={certPassword}
                onChange={(e) => setCertPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertUploadEnv(null)} disabled={certUploading}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCertUpload} disabled={certUploading || !certFile}>
              {certUploading && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.settings.uploadCertificate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate delete confirm */}
      <AlertDialog
        open={certDeleteEnv !== null}
        onOpenChange={(open) => {
          if (!open && !certDeleting) setCertDeleteEnv(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('fiscal.settings.deleteCertificateConfirmTitle', { environment: certDeleteEnv ? t(`fiscal.environment.${certDeleteEnv}`) : '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('fiscal.settings.deleteCertificateConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={certDeleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={certDeleting} variant="destructive" onClick={handleCertDelete}>
              {certDeleting && <Loader2 className="size-4 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set numbering counter (migration continuity) */}
      <Dialog
        open={counterDialogOpen}
        onOpenChange={(open) => {
          if (!counterSubmitting) setCounterDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fiscal.settings.setCounterTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2.5">
              <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-700 dark:text-amber-400">{t('fiscal.settings.setCounterWarning')}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="counter-environment">{t('fiscal.settings.counterEnvironmentLabel')}</Label>
              {/* Bez preselekcji — świadomy wybór środowiska jest częścią seedu. */}
              <Select value={counterEnv || undefined} onValueChange={setCounterEnv}>
                <SelectTrigger id="counter-environment" className="w-full">
                  <SelectValue placeholder={t('fiscal.settings.counterEnvironmentPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {KSEF_ENVIRONMENTS.map((env) => (
                    <SelectItem key={env} value={env}>
                      {t(`fiscal.environment.${env}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="counter-prefix">{t('fiscal.settings.counterPrefixInputLabel')}</Label>
              <Input
                id="counter-prefix"
                value={counterPrefix}
                onChange={(e) => setCounterPrefix(e.target.value)}
                placeholder="EBS"
                maxLength={10}
                aria-invalid={counterPrefixError ? true : undefined}
              />
              {counterPrefixError && <p className="text-destructive text-xs">{counterPrefixError}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="counter-year">{t('fiscal.settings.counterYearLabel')}</Label>
                <Input
                  id="counter-year"
                  type="number"
                  min={MIN_COUNTER_YEAR}
                  max={MAX_COUNTER_YEAR}
                  value={counterYear}
                  onChange={(e) => setCounterYear(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="counter-month">{t('fiscal.settings.counterMonthLabel')}</Label>
                <Input
                  id="counter-month"
                  type="number"
                  min={1}
                  max={12}
                  value={counterMonth}
                  onChange={(e) => setCounterMonth(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="counter-last-seq">{t('fiscal.settings.counterLastSeqLabel')}</Label>
              <Input
                id="counter-last-seq"
                type="number"
                min={0}
                max={MAX_COUNTER_SEQ}
                value={counterLastSeq}
                onChange={(e) => setCounterLastSeq(e.target.value)}
                placeholder="714"
              />
              <p className="text-muted-foreground text-xs">{t('fiscal.settings.counterLastSeqHint')}</p>
            </div>
            {counterPreview && (
              <p className="text-muted-foreground text-xs">
                {t('fiscal.settings.counterNextPreview', { example: counterPreview })}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCounterDialogOpen(false)} disabled={counterSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSetCounter} disabled={counterSubmitting || !counterFormValid}>
              {counterSubmitting && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.settings.setCounter')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create API token */}
      <Dialog
        open={tokenDialogOpen}
        onOpenChange={(open) => {
          if (!tokenCreating) setTokenDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fiscal.settings.addTokenTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="token-name">{t('fiscal.settings.tokenNameLabel')}</Label>
              <Input
                id="token-name"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder={t('fiscal.settings.tokenNamePlaceholder')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="token-prefixes">{t('fiscal.settings.tokenNumberingPrefixesLabel')}</Label>
              <Input
                id="token-prefixes"
                value={tokenPrefixesInput}
                onChange={(e) => setTokenPrefixesInput(e.target.value)}
                placeholder={t('fiscal.settings.tokenNumberingPrefixesPlaceholder')}
                aria-describedby="token-prefixes-help"
              />
              <p id="token-prefixes-help" className="text-muted-foreground text-xs">
                {t('fiscal.settings.tokenNumberingPrefixesHelp')}
              </p>
            </div>
            <TokenEnvSelect
              id="token-environment"
              value={tokenEnvValue}
              onChange={setTokenEnvValue}
              warning={envCertWarning(tokenEnvValue)}
              companyDefaultEnv={settings.environment}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTokenDialogOpen(false)} disabled={tokenCreating}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateToken} disabled={tokenCreating || !tokenName.trim()}>
              {tokenCreating && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.settings.createToken')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit API token */}
      <Dialog
        open={editTarget !== null}
        onOpenChange={(open) => {
          if (!editSaving && !open) setEditTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fiscal.settings.editTokenTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="edit-token-name">{t('fiscal.settings.tokenNameLabel')}</Label>
              <Input
                id="edit-token-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t('fiscal.settings.tokenNamePlaceholder')}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-token-prefixes">{t('fiscal.settings.tokenNumberingPrefixesLabel')}</Label>
              <Input
                id="edit-token-prefixes"
                value={editPrefixesInput}
                onChange={(e) => setEditPrefixesInput(e.target.value)}
                placeholder={t('fiscal.settings.tokenNumberingPrefixesPlaceholder')}
                aria-describedby="edit-token-prefixes-help"
              />
              <p id="edit-token-prefixes-help" className="text-muted-foreground text-xs">
                {t('fiscal.settings.tokenNumberingPrefixesHelp')}
              </p>
            </div>
            <TokenEnvSelect
              id="edit-token-environment"
              value={editEnvValue}
              onChange={setEditEnvValue}
              warning={envCertWarning(editEnvValue)}
              companyDefaultEnv={settings.environment}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={editSaving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleUpdateToken} disabled={editSaving || !editName.trim()}>
              {editSaving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke API token confirm */}
      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !revoking) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('fiscal.settings.revokeConfirmTitle', { name: revokeTarget?.name ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('fiscal.settings.revokeConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={revoking} variant="destructive" onClick={handleRevokeToken}>
              {revoking && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.settings.revoke')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* API token reveal */}
      <AlertDialog open={tokenReveal !== null} onOpenChange={(open) => !open && setTokenReveal(null)}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('fiscal.settings.tokenRevealTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('fiscal.settings.tokenRevealDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          {tokenReveal && (
            <div className="space-y-3 text-left">
              <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2.5">
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs text-amber-700 dark:text-amber-400">{t('fiscal.settings.tokenRevealWarning')}</p>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-muted flex-1 overflow-x-auto rounded px-2 py-1.5 font-mono text-xs">{tokenReveal}</code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(tokenReveal, 'fiscal.settings.tokenCopied')}>
                  <Copy className="size-4" />
                  {t('fiscal.settings.copyToken')}
                </Button>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setTokenReveal(null)}>{t('fiscal.settings.close')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
